import type { Locale } from '$lib/i18n/types';
import { browser, dev } from '$app/environment';
import { env } from '$env/dynamic/public';
import { partner1Branding, partner2Branding } from '$lib/constants/sponsoredPoi';
import {
	type AvailableLanguageTag,
	isAvailableLanguageTag,
	setLanguageTag,
	sourceLanguageTag,
} from '$paraglide/runtime';
import {
	error,
	type Handle,
	redirect,
	type Reroute,
} from '@sveltejs/kit';

interface PathMapping {
	url: string;
	label: string;
	route: string;
	sub?: PathMapping[];
}

const DEFAULT_LOCALE = 'en-US';

const pathMappingsForPartner: PathMapping[] = [
	{ route: 'PARTNER', url: partner1Branding.id, label: partner1Branding.name },
	{ route: 'PARTNER2', url: partner2Branding.id, label: partner2Branding.name },
] as const;

/**
 * localized path mappings
 * These are prefixes and used to bidirectionally convert route to and from localized path
 */
export const PATH_MAPPINGS: Record<AvailableLanguageTag, PathMapping[]> = {
	de: [
		{
			route: 'map',
			url: 'karten-stadtplan',
			label: 'Karten Stadtplan',
			sub: [
				{ route: 'ACCOMMODATION', url: 'hotels', label: 'Hotels' },
				{ route: 'EV_SERVICE', url: 'ladestationen-e-auto', label: 'Ladestationen E-Auto' },
				{ route: 'PARKING', url: 'parkplatze', label: 'Parkplätze' },
				{ route: 'RESTAURANT', url: 'restaurants', label: 'Restaurants' },
				{ route: 'SERVICE_GAS_STATION', url: 'tankstellen', label: 'Tankstellen' },
				{ route: 'TOURISM', url: 'touristische-attraktionen', label: 'Touristische Attraktionen' },
				{ route: 'traffic', url: 'verkehr', label: 'Verkehr' },
				...pathMappingsForPartner,
			],
		},
		{
			route: 'routes',
			url: 'routenplaner',
			label: 'Routenplaner',
			sub: [{ route: 'results', url: 'ergebnisse', label: 'Ergebnisse' }],
		},
	],
	en: [
		{
			route: 'map',
			url: 'maps',
			label: 'Maps',
			sub: [
				{ route: 'ACCOMMODATION', url: 'hotels', label: 'Hotels' },
				{ route: 'EV_SERVICE', url: 'ev-charging-stations', label: 'EV charging stations' },
				{ route: 'PARKING', url: 'car-parks', label: 'Car parks' },
				{ route: 'RESTAURANT', url: 'restaurants', label: 'Restaurants' },
				{ route: 'SERVICE_GAS_STATION', url: 'service-stations', label: 'Gas stations' },
				{ route: 'TOURISM', url: 'tourist-attractions', label: 'Tourist attractions' },
				{ route: 'traffic', url: 'traffic', label: 'Traffic' },
				...pathMappingsForPartner,
			],
		},
		{
			route: 'routes',
			url: 'routes',
			label: 'Routes',
			sub: [{ route: 'results', url: 'results', label: 'Results' }],
		},
	],
	es: [
		{
			route: 'map',
			url: 'mapas-planos',
			label: 'Mapas Planos',
			sub: [
				{ route: 'ACCOMMODATION', url: 'hoteles', label: 'Hoteles' },
				{
					route: 'EV_SERVICE',
					url: 'puntos-recarga-coche-electrico',
					label: 'Puntos recarga coche electrico',
				},
				{ route: 'PARKING', url: 'aparcamientos', label: 'Aparcamientos' },
				{ route: 'RESTAURANT', url: 'restaurantes', label: 'Restaurantes' },
				{ route: 'SERVICE_GAS_STATION', url: 'estaciones-servicio', label: 'Estaciones-servicio' },
				{ route: 'TOURISM', url: 'lugares-turisticos', label: 'Lugares Turisticos' },
				{ route: 'traffic', url: 'trafico', label: 'Trafico' },
				...pathMappingsForPartner,
			],
		},
		{
			route: 'routes',
			url: 'itinerarios',
			label: 'Itinerarios',
			sub: [{ route: 'results', url: 'resultados', label: 'Resultados' }],
		},
	],
	fr: [
		{
			route: 'map',
			url: 'cartes-plans',
			label: 'Cartes et plans',
			sub: [
				{ route: 'ACCOMMODATION', url: 'hotels', label: 'Hôtels' },
				{ route: 'EV_SERVICE', url: 'electrique-service', label: 'Service électrique' },
				{ route: 'PARKING', url: 'parkings', label: 'Parkings' },
				{ route: 'RESTAURANT', url: 'restaurants', label: 'Restaurants' },
				{ route: 'SERVICE_GAS_STATION', url: 'stations-service', label: 'Stations-service' },
				{ route: 'TOURISM', url: 'sites-touristiques', label: 'Sites touristiques' },
				{ route: 'traffic', url: 'trafic', label: 'Trafic' },
				...pathMappingsForPartner,
			],
		},
		{
			route: 'routes',
			url: 'itineraires',
			label: 'Itinéraires',
			sub: [{ route: 'results', url: 'resultats', label: 'Resultats' }],
		},
	],
	it: [
		{
			route: 'map',
			url: 'mappe-piantine',
			label: 'Mappe Piantine',
			sub: [
				{ route: 'ACCOMMODATION', url: 'alberghi', label: 'Alberghi' },
				{
					route: 'EV_SERVICE',
					url: 'stazioni-ricarica-veicoli-elettrici',
					label: 'Stazioni ricarica veicoli elettrici',
				},
				{ route: 'PARKING', url: 'parcheggi', label: 'Parcheggi' },
				{ route: 'RESTAURANT', url: 'ristoranti', label: 'Ristoranti' },
				{
					route: 'SERVICE_GAS_STATION',
					url: 'stazioni-di-servizio',
					label: 'Stazioni-di-servizio',
				},
				{ route: 'TOURISM', url: 'siti-turistici', label: 'Siti-Turistici' },
				{ route: 'traffic', url: 'traffico', label: 'Traffico' },
				...pathMappingsForPartner,
			],
		},
		{
			route: 'routes',
			url: 'itinerari',
			label: 'Itinerari',
			sub: [{ route: 'results', url: 'risultati', label: 'Risultati' }],
		},
	],
	nl: [
		{
			route: 'map',
			url: 'kaarten-plattegronden',
			label: 'Kaarten',
			sub: [
				{ route: 'ACCOMMODATION', url: 'hotels', label: 'Hotels' },
				{
					route: 'EV_SERVICE',
					url: 'laadstation-elektrische-voertuigen',
					label: 'Laadstation elektrische voertuigen',
				},
				{ route: 'PARKING', url: 'parkings', label: 'Parkings' },
				{ route: 'RESTAURANT', url: 'restaurants', label: 'Restaurants' },
				{ route: 'SERVICE_GAS_STATION', url: 'tankstations', label: 'Tankstations' },
				{
					route: 'TOURISM',
					url: 'toeristische-bezienswaardigheden',
					label: 'Toeristische-Bezienswaardigheden',
				},
				{ route: 'traffic', url: 'verkeer', label: 'Verkeer' },
				...pathMappingsForPartner,
			],
		},
		{
			route: 'routes',
			url: 'routes',
			label: 'Routes',
			sub: [{ route: 'results', url: 'resultaten', label: 'Resultaten' }],
		},
	],
	pt: [
		{
			route: 'map',
			url: 'mapas-plantas',
			label: 'Mapas Plantas',
			sub: [
				{ route: 'ACCOMMODATION', url: 'hoteis', label: 'Hoteis' },
				{
					route: 'EV_SERVICE',
					url: 'postos-carregamento-eletrico',
					label: 'Postos carregamento eletrico',
				},
				{ route: 'PARKING', url: 'estacionamento', label: 'Estacionamento' },
				{ route: 'RESTAURANT', url: 'restaurantes', label: 'Restaurantes' },
				{ route: 'SERVICE_GAS_STATION', url: 'estacoes-servico', label: 'Estacoes servico' },
				{ route: 'TOURISM', url: 'sitios-turisticos', label: 'Sitios Turisticos' },
				{ route: 'traffic', url: 'trafego', label: 'Trafego' },
				...pathMappingsForPartner,
			],
		},
		{
			route: 'routes',
			url: 'itinerarios',
			label: 'Itinerarios',
			sub: [{ route: 'results', url: 'resultados', label: 'Resultados' }],
		},
	],
};

/**
 * language for each hostname
 */
export const HOSTNAME_LOCALE: Record<string, Locale> = {
	'www.viamichelin.at': 'de-AT',
	'fr.viamichelin.be': 'fr-BE',
	'nl.viamichelin.be': 'nl-BE',
	'de.viamichelin.ch': 'de-CH',
	'fr.viamichelin.ch': 'fr-CH',
	'it.viamichelin.ch': 'it-CH',
	'www.viamichelin.com': 'en-US',
	'www.viamichelin.de': 'de-DE',
	'www.viamichelin.es': 'es-ES',
	'www.viamichelin.fr': 'fr-FR',
	'www.viamichelin.it': 'it-IT',
	'www.viamichelin.ie': 'en-IE',
	'www.viamichelin.nl': 'nl-NL',
	'www.viamichelin.pt': 'pt-PT',
	'www.viamichelin.co.uk': 'en-GB',
};

const HOSTNAME_LOCALE_BETA: Record<string, Locale> = {
	'beta.viamichelin.fr': 'fr-FR',
	'beta.viamichelin.ie': 'en-IE',
	'fr.beta.viamichelin.be': 'fr-BE',
	'nl.beta.viamichelin.be': 'nl-BE',
	'beta.viamichelin.at': 'de-AT',
	'de.beta.viamichelin.ch': 'de-CH',
	'fr.beta.viamichelin.ch': 'fr-CH',
	'it.beta.viamichelin.ch': 'it-CH',
	'beta.viamichelin.co.uk': 'en-GB',
	'beta.viamichelin.de': 'de-DE',
	'beta.viamichelin.es': 'es-ES',
	'beta.viamichelin.it': 'it-IT',
	'beta.viamichelin.nl': 'nl-NL',
	'beta.viamichelin.pt': 'pt-PT',
	'beta.viamichelin.com': 'en-US',
};

const HOSTNAME_LOCALE_REVIEW_PREFIX: Record<string, Locale> = {
	at: 'de-AT',
	com: 'en-US',
	couk: 'en-GB',
	de: 'de-DE',
	dech: 'de-CH',
	es: 'es-ES',
	fr: 'fr-FR',
	frbe: 'fr-BE',
	frch: 'fr-CH',
	ie: 'en-IE',
	it: 'it-IT',
	itch: 'it-CH',
	nl: 'nl-NL',
	nlbe: 'nl-BE',
	pt: 'pt-PT',
};

const ENABLED_TLDS = env.PUBLIC_ENABLED_TLDS?.split(',').map(tld => `.${tld}`);

/**
 * technical paths that must not be localized or rerouted
 */
const IGNORED_PATHS = [
	// internal endpoints and pages
	'/auth',
	'/authentication',
	'/healthcheck',
	// well known paths that are requested by crawlers or browsers without being explicitly linked
	'/favicon.ico',
	'/robots.txt',
];

const knownHostnames = Object.keys(HOSTNAME_LOCALE);
const tldsWithoutHostname = ENABLED_TLDS?.filter(
	tld => !knownHostnames.some(hostname => hostname.endsWith(tld)),
);

if (tldsWithoutHostname?.length) {
	throw new Error(
		`The following enabled TLD have no hostname set up: ${tldsWithoutHostname.join(', ')}`,
	);
}

export function segments2routeSegments(
	segments: string[],
	lang: AvailableLanguageTag,
	reverse = false,
): { value: string; label?: string }[] {
	let mapping = PATH_MAPPINGS[lang];
	const result = [];
	// go over path segments, as long as there is an entry, continue to collect the replacements
	for (let i = 0; i < segments.length; i++) {
		const segment = segments[i];
		if (segment === '') {
			result.push({ label: undefined, value: '' });
			continue;
		}
		const pathMapping = mapping.find(m => (reverse ? m.route : m.url) === segment);
		if (!pathMapping) {
			break;
		}
		result.push(
			reverse
				? { label: pathMapping.label, value: pathMapping.url }
				: { label: pathMapping.label, value: pathMapping.route },
		);

		if (pathMapping.sub) {
			mapping = pathMapping.sub;
		} else {
			break;
		}
	}

	return result;
}

export function getPathLabel(path: string, lang: AvailableLanguageTag) {
	const segments = segments2routeSegments(path.split('/'), lang, true);
	return segments[segments.length - 1]?.label;
}

/**
 * localized to route path or reverse
 * @returns {string} pathname only, no params or hostname
 */
export function url2route(pathname: string, lang: AvailableLanguageTag, reverse = false): string {
	const segments = pathname.split('/');
	const result = segments2routeSegments(segments, lang, reverse).map(segment => segment.value);

	if (result.length > 0) {
		return pathname.replace(segments.slice(0, result.length).join('/'), result.join('/'));
	}
	return pathname;
}

// used to contruct URL instances
const BASE_DUMMY = 'http://example.com';
/**
 * localize href
 * creates a new URL with pathname converted to localized
 */
export function i18nPath(abolutePath: string, lang: AvailableLanguageTag) {
	if (dev && abolutePath[0] !== '/') {
		throw new Error(
			`i18nPath must be called with an absolute path starting with / but got "${abolutePath}"`,
		);
	}
	const url = new URL(abolutePath, BASE_DUMMY);
	if (url.pathname === '/map') {
		url.pathname = '/';
	} else {
		url.pathname = url2route(url.pathname, lang, true);
	}
	return url.toString().slice(BASE_DUMMY.length);
}

export function i18nFullHref(absolutePath: string, lang: AvailableLanguageTag, origin: string) {
	const path = i18nPath(absolutePath, lang);
	return `${origin}${path}`;
}

/**
 * Use the TLD to determine whether or not the hostname is a review env
 * @param hostname
 */
export function isReviewOrLocalHostname(hostname: string) {
	return hostname.endsWith('.xyz') || hostname.includes('local.app');
}

/**
 * on dev or staging, lang can be set with a query param
 * because it is also used on the client, we can only use hostname to identify
 * @param hostname
 */
export function isLocaleFallbackAllowed(hostname: string) {
	return dev
		|| hostname === 'localhost'
		|| hostname.includes('local')
		|| hostname.endsWith('.michelin.net')
		|| isReviewOrLocalHostname(hostname);
}

/**
 * determine the language to use for this url.
 * @param url
 */
function getLang(url: URL): AvailableLanguageTag {
	if (browser) {
		// take value from ssr rendered lang
		return document.documentElement.lang as AvailableLanguageTag;
	} else {
		return (locale2lang(getLocale(url)) ?? sourceLanguageTag);
	}
}

/**
 * uses the hostname
 * @param url
 */
export function getLocale(url: URL): Locale {
	// SSR, determine from hostname
	const hostname = url.hostname;
	const locale = getHostnameLocale(hostname);
	if (locale) {
		return locale;
	} else if (isLocaleFallbackAllowed(hostname) && process?.env?.PUBLIC_APP_LOCALE) {
		// do not use sveltekit env here, we don't set this in our containers and sveltekit would error
		const envLocale = process.env.PUBLIC_APP_LOCALE as Locale;
		if (isAvailableLanguageTag(locale2lang(envLocale))) {
			return envLocale;
		}
	}
	return DEFAULT_LOCALE;
}

export function getHostnameLocale(hostname: string): Locale | undefined {
	if (hostname.includes('beta.')) {
		return HOSTNAME_LOCALE_BETA[hostname];
	} else if (isReviewOrLocalHostname(hostname)) {
		const parts = hostname.split('-', 1);
		if (HOSTNAME_LOCALE_REVIEW_PREFIX[parts[0]]) {
			return HOSTNAME_LOCALE_REVIEW_PREFIX[parts[0]];
		}
	} else {
		return HOSTNAME_LOCALE[hostname];
	}
}

export function getAlternateHostnames(hostname: string): [string, Locale][] {
	const map = hostname.includes('beta.') ? HOSTNAME_LOCALE_BETA : HOSTNAME_LOCALE;
	return Object.entries(map).filter(([host, _locale]) => {
		return hostname !== host && (!ENABLED_TLDS || ENABLED_TLDS.some(tld => host.endsWith(tld)));
	});
}

export function locale2lang(locale: Locale): AvailableLanguageTag {
	return locale?.split('-', 1)[0] as AvailableLanguageTag;
}

function isIgnored(path: string) {
	return IGNORED_PATHS.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * sveltekit reroute hook
 *
 * ensures /map is served on / and paths are localized according to the mapping
 */
type RerouteBase = (event: Pick<Parameters<Reroute>[0], 'url'>) => ReturnType<Reroute>;

export const reroute: RerouteBase = ({ url }) => {
	if (isIgnored(url.pathname)) {
		return;
	}

	if (url.pathname === '/') {
		return '/map';
	}

	if (url.pathname === '/__data.json') {
		return '/map/__data.json';
	}

	return url2route(url.pathname, getLang(url));
};

/**
 * sveltekit server hook
 *
 * determines and stores request language
 * redirects access on localized /map to / to avoid two paths to the same page
 * prevents access to non-localized paths that have a localization eg /routes for french (must be /itineraires)
 */
export const i18nRoutingHandle = (async ({ event, resolve }) => {
	// make sure locals.lang is always set
	const locale = getLocale(event.url);
	const lang = locale2lang(locale);
	event.locals.lang = lang;
	event.locals.locale = locale;
	// but ignore everything
	if (isIgnored(event.url.pathname)) {
		return resolve(event);
	}

	const routePath = url2route(event.url.pathname, lang);
	if (routePath === event.url.pathname) {
		// not rerouted, validate that no mapping exists
		const mapping = PATH_MAPPINGS[lang].find(mapping => routePath.startsWith(mapping.route));
		if (mapping) {
			// accessing the route should always be done by the localized path and a mapping exists
			error(404, 'not found'); // TODO translate not found message, or even 301 instead?
		}
	}

	if (routePath === '/map') {
		const url = new URL(event.url);
		url.pathname = '/';
		redirect(301, url);
	}

	// in +layout.svelte the actual value is set, read from the value we determined here
	setLanguageTag(() => {
		throw new Error(
			'must not use translation functions in SSR before +layout.svelte is called to avoid crosstalk between different user requests',
		);
	});
	return resolve(event, {
		transformPageChunk({ done, html }) {
			// Only do it at the very end of the rendering process
			if (done) {
				return html.replace('%lang%', lang);
			}
		},
	});
}) satisfies Handle;
